import type { ApiOnboardingGraph, PlaybookVersionNoGraph } from '@onefootprint/request-types/dashboard';
import JSON5 from 'json5';
import type { CreateOnboardingBody, RunFormValues } from './onboarding.types';

// Mirrors what POST /org/onboardings accepts (no identify flow: no auth, no internal matching).
// A KYB playbook that KYCs its beneficial owners is rejected there — the API can't prompt an owner
// through their own KYC — so those stay on the legacy test mode.
export const isOnboardingEligible = (playbook: PlaybookVersionNoGraph, graph: ApiOnboardingGraph): boolean =>
  (playbook.kind === 'kyc' ||
    playbook.kind === 'document' ||
    (playbook.kind === 'kyb' && !graph.nodes.some(node => node.kind === 'bo_kyc'))) &&
  !playbook.requiresAuthentication &&
  playbook.requiredAuthMethods.length === 0 &&
  !playbook.settings.supportInternalMatching;

export const parseJson5Object = (value?: string): Record<string, unknown> => {
  if (!value?.trim()) return {};
  const parsed = JSON5.parse(value);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new SyntaxError('Expected a JSON object');
  }
  return parsed as Record<string, unknown>;
};

// Mirrors the backend's third party auth gate in `api/core/src/utils/token.rs`: the attested event
// stands in for the phone challenge the playbook would otherwise run, so it must actually run one.
// Modern playbooks challenge on the graph; legacy ones from their settings, matched exactly there.
export const requiresPhoneOtp = (playbook: PlaybookVersionNoGraph, graph: ApiOnboardingGraph): boolean => {
  const { requiresAuthentication, requiredAuthMethods } = playbook;
  return (
    graph.nodes.some(n => n.kind === 'authentication' && n.configs.some(c => c.kind === 'phone')) ||
    (requiresAuthentication && requiredAuthMethods.length === 1 && requiredAuthMethods[0] === 'phone')
  );
};

// Mirrors the backend's `get_verified_prefill_settings`: a graph runs Verified from a dedicated node
// or from an identity data node with prefill enabled. Attesting the consent is pointless otherwise.
export const runsVerifiedPrefill = (graph: ApiOnboardingGraph): boolean =>
  graph.nodes.some(n => n.kind === 'verified_prefill' || (n.kind === 'identity_data' && n.enabledVerifiedPrefill));

// Sets `values` in the vault data editor's JSON5 text. A line already carrying the key is rewritten
// in place, uncommenting it when it's one of the template's commented-out examples, so the rest of
// the template survives. New keys go under the opening brace, the one spot where the trailing comma
// they carry stays valid whatever follows.
export const withVaultDataValues = (source: string, values: Record<string, string>): string => {
  const keyOf = (line: string) =>
    line
      .trim()
      .replace(/^\/\/\s*/, '')
      .match(/^"([^"]+)"\s*:/)?.[1];
  const entryFor = (key: string) => `  "${key}": ${JSON.stringify(values[key])},`;

  const lines = source.split('\n');
  const rewritten = lines.map(line => {
    const key = keyOf(line);
    return key && Object.hasOwn(values, key) ? entryFor(key) : line;
  });
  const missing = Object.keys(values).filter(key => !lines.some(line => keyOf(line) === key));
  const openIndex = rewritten.findIndex(line => line.trim().endsWith('{'));
  rewritten.splice(openIndex + 1, 0, ...missing.map(entryFor));
  return rewritten.join('\n');
};

// idv2's `identify.otp.verifiedPrefill.disclaimer` with its SSN clause, for parity with the hosted flow.
const VERIFIED_PREFILL_DISCLAIMER =
  "By continuing, I agree that Verified (Footprint's service provider) and its vendors may receive my personal information and autofill more info about me, including my social security number.";

type BuildOnboardingBodyArgs = {
  fpId: string;
  playbook: PlaybookVersionNoGraph;
  values: RunFormValues;
  isLive: boolean;
  hasPendingChanges?: boolean;
  versionId?: string;
  // Stamps the attestations below. Passed in rather than read from the clock so the curl preview,
  // which rebuilds on every keystroke, doesn't churn.
  now?: string;
};

export const buildOnboardingBody = ({
  fpId,
  playbook,
  values,
  isLive,
  hasPendingChanges = false,
  versionId,
  now,
}: BuildOnboardingBodyArgs): CreateOnboardingBody => {
  const prerequisiteData = parseJson5Object(values.prerequisiteData);
  const timeout = values.synchronousTimeoutSecs;
  const hasTimeout = timeout !== null && timeout !== undefined && timeout !== '';
  return {
    fp_id: fpId,
    // An in-progress draft has no version tag yet, so address it by its version id; otherwise use
    // the version number, which runs the latest saved version.
    key: hasPendingChanges && versionId ? `${playbook.key}:${versionId}` : `${playbook.key}:v${playbook.version}`,
    onboarding_external_id: values.onboardingExternalId || undefined,
    // Sandbox-only; when omitted in sandbox, the backend evaluates the playbook's real rules.
    fixture_result: !isLive && values.sandboxOutcome ? values.sandboxOutcome : undefined,
    // The API rejects prerequisite_data unless the playbook has a prerequisite node, so omit it when empty.
    prerequisite_data: Object.keys(prerequisiteData).length > 0 ? prerequisiteData : undefined,
    // Without a timeout the API queues the run and immediately returns `pending`.
    synchronous_timeout_secs: hasTimeout ? Number(timeout) : undefined,
    // `sms` is the only kind the API accepts.
    third_party_auth: values.thirdPartyAuth && now ? { kind: 'sms', timestamp: now } : undefined,
    // The API also accepts terms_and_conditions, but Verified prefill is the only consent test mode needs.
    consent:
      values.verifiedPrefillConsent && now
        ? [{ kind: 'verified_prefill', content: VERIFIED_PREFILL_DISCLAIMER, timestamp: now }]
        : undefined,
  };
};
